import { Select } from "antd";
import type { Block, Building, Section } from "../types/objects.types";

interface RoomsFilterProps {
	buildings: Building[];
	blocks: Block[];
	sections: Section[];
	selectedBuildingId: string;
	selectedBlockId: string;
	selectedSectionId: string;
	selectedStatus: string;
	selectedRoomsCount: string;
	onBuildingBlockChange: (buildingId: string, blockId: string) => void;
	onSectionChange: (sectionId: string) => void;
	onStatusChange: (status: string) => void;
	onRoomsCountChange: (count: string) => void;
	counts: {
		sotuvda: number;
		sotilgan: number;
		band: number;
		bekor: number;
	};
}

export function RoomsFilter({
	buildings,
	blocks,
	sections,
	selectedBuildingId,
	selectedBlockId,
	selectedSectionId,
	selectedStatus,
	selectedRoomsCount,
	onBuildingBlockChange,
	onSectionChange,
	onStatusChange,
	onRoomsCountChange,
	counts,
}: RoomsFilterProps) {
	// Concatenate building name and block name for options
	const buildingBlockOptions = blocks.map((block) => {
		const building = buildings.find((b) => b.id === block.buildingId);
		const buildingName = building ? building.name : "";
		return {
			label: `${buildingName} · ${block.name}`,
			value: `${block.buildingId}|${block.id}`,
		};
	});

	const activeBuildingBlockValue =
		selectedBuildingId && selectedBlockId ? `${selectedBuildingId}|${selectedBlockId}` : undefined;
	const safeBuildingBlockValue = buildingBlockOptions.some(
		(option) => option.value === activeBuildingBlockValue
	)
		? activeBuildingBlockValue
		: undefined;

	// Get sections for current block
	const filteredSections = sections.filter((s) => s.blockId === selectedBlockId);
	const sectionOptions = [
		{ label: "Barcha padezdlar", value: "all" },
		...filteredSections.map((s) => ({
			label: s.name,
			value: s.id,
		})),
	];
	const safeSectionValue = sectionOptions.some((option) => option.value === selectedSectionId)
		? selectedSectionId
		: undefined;

	return (
		<div className="crm-card crm-rooms-filter" style={{ marginBottom: "16px" }}>
			<div className="crm-card-body crm-rooms-filter-body">
				<Select
					className="crm-rooms-filter-select wide"
					value={safeBuildingBlockValue}
					placeholder="Bino va blok tanlang"
					onChange={(value) => {
						const [bId, blkId] = value.split("|");
						onBuildingBlockChange(bId, blkId);
					}}
					options={buildingBlockOptions}
				/>

				<Select
					className="crm-rooms-filter-select"
					value={safeSectionValue}
					placeholder="Padezd tanlang"
					onChange={onSectionChange}
					options={sectionOptions}
				/>

				<Select
					className="crm-rooms-filter-select"
					value={selectedStatus}
					onChange={onStatusChange}
					options={[
						{ label: "Barcha holat", value: "all" },
						{ label: "Bo'sh (sotuvda)", value: "sotuvda" },
						{ label: "Sotilgan", value: "sotilgan" },
						{ label: "Band qilingan", value: "band" },
						{ label: "Bekor qilingan", value: "bekor" },
					]}
				/>

				<Select
					className="crm-rooms-filter-select"
					value={selectedRoomsCount}
					onChange={onRoomsCountChange}
					options={[
						{ label: "Barcha xona", value: "all" },
						{ label: "1-xonali", value: "1" },
						{ label: "2-xonali", value: "2" },
						{ label: "3-xonali", value: "3" },
						{ label: "4-xonali", value: "4" },
					]}
				/>

				<div className="grow" />

				<div className="legend crm-rooms-legend">
					<span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
						<span className="dot green" /> Bo'sh: {counts.sotuvda}
					</span>
					<span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
						<span className="dot amber" /> Band: {counts.band}
					</span>
					<span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
						<span className="dot slate" /> Sotilgan: {counts.sotilgan}
					</span>
					<span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
						<span className="dot red" /> Bekor: {counts.bekor}
					</span>
				</div>
			</div>
		</div>
	);
}
